"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "../ui/OptimizedImage";

/**
 * "What else is happening around town" — the curated external-events directory.
 * Shows only admin-approved scraped events for the selected city (same data the
 * mobile app and map use). Hidden entirely when nothing is approved yet.
 */

type DirectoryEvent = {
  id: string;
  title: string;
  type: string;
  detailPath?: string;
  imageUrl?: string | null;
  venue?: string | null;
  startsAt?: string | null;
  priceTier?: number | null;
};

function slugify(s: string): string {
  return (
    s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "jaipur"
  );
}

function formatDate(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}

export function WhatElseSection({ city }: { city?: string }) {
  const [events, setEvents] = useState<DirectoryEvent[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const slug = slugify(city || "jaipur");
    fetch(`/api/directory/events?city=${encodeURIComponent(slug)}&limit=24`)
      .then((r) => (r.ok ? r.json() : { events: [] }))
      .then((d: { events?: DirectoryEvent[] }) => {
        if (!cancelled) setEvents(Array.isArray(d.events) ? d.events : []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [city]);

  // Hidden until an admin approves at least one event for this city.
  if (!loaded || events.length === 0) return null;

  return (
    <section className="px-4 py-6 md:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--hopp-text)]">
            What else is happening around town
          </h2>
          <Link href="/directory" className="text-xs font-semibold text-[var(--hopp-sapphire)] hover:underline">
            See all
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {events.map((ev) => {
            const date = formatDate(ev.startsAt);
            return (
              <Link
                key={ev.id}
                href={ev.detailPath ?? "#"}
                className="group flex w-44 flex-shrink-0 flex-col overflow-hidden rounded-xl border border-white/[0.06] bg-[var(--hopp-panel)] transition hover:border-white/15"
              >
                <div className="relative h-28 w-full overflow-hidden bg-[var(--hopp-panel-strong)]">
                  {ev.imageUrl ? (
                    <Image src={ev.imageUrl} alt={ev.title} fill className="object-cover transition group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xl text-white/20">★</div>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1 p-3">
                  <span className="truncate text-sm font-semibold text-[var(--hopp-text)]">{ev.title}</span>
                  <span className="truncate text-xs text-[var(--hopp-muted)]">
                    {date ? date : ""}
                    {ev.venue ? `${date ? " · " : ""}${ev.venue}` : ""}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
